import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StickyEditBanner } from "@/components/ui/StickyEditBanner";
import { ListOverviewCard } from "./ListOverviewCard";
import { ListEditTabs } from "./ListEditTabs";
import { List } from "@/types/list";

interface ListProfileProps {
  list: List;
  isEditing: boolean;
  onChange: (list: any) => void;
  onSave: () => void;
  onCancel: () => void;
  customFieldsConfig?: any[];
}

export const ListProfile: React.FC<ListProfileProps> = ({
  list,
  isEditing,
  onChange,
  onSave,
  onCancel,
  customFieldsConfig = []
}) => {
  const { t } = useTranslation();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!bannerRef.current) return;
      const rect = bannerRef.current.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleFieldChange = (field: string, value: string) => {
    onChange({ ...list, [field]: value });
  };

  const handleCustomFieldsChange = (customFields: Record<string, any>) => {
    onChange({ ...list, custom_fields: customFields });
  };

  return (
    <div className="flex flex-col gap-6">
      {isEditing && (
        <>
          <StickyEditBanner
            isSticky={isSticky}
            onSave={onSave}
            onCancel={onCancel}
          >
            {t('common.editMode', 'Edit mode')}
          </StickyEditBanner>
        </>
      )}
      
      <div className={`w-full bg-white dark:bg-gray-900 rounded-sm shadow p-6 relative ${isEditing ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-950' : ''}`}>
        {isEditing ? (
          <div className="space-y-6">
            <ListEditTabs
              list={list}
              isEditing={isEditing}
              onChange={handleFieldChange}
              onCustomFieldsChange={handleCustomFieldsChange}
              customFieldsConfig={customFieldsConfig}
            />
          </div>
        ) : (
          <ListOverviewCard list={list} customFieldsConfig={customFieldsConfig} />
        )}
      </div>
    </div>
  );
};
